import React, {useState, useContext, useCallback, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {EventCreateContext} from '~/CreateFabricInvitation/routes/EventCreateDesign/EventCreateContext';
import {UPDATE_DESIGN_OPTIONS} from '~/CreateFabricInvitation/reducers/fabricDesign/constants';
import {useImageUpload} from '~/CreateFabricInvitation/components/PhotoEditor/common/useImageUpload';
import {
  selectCountOfUyoImages,
  selectFabricArtBackgroundImage,
} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {MainPhotoEditorMenu} from './MainPhotoEditorMenu';
import {TransformMenu} from './TransformMenu/TransformMenu';
import {ImageFilters} from '../../actions/UyoControl/ImageFilters/ImageFilters';
import {TRANSFORM_TYPES, MENU_TYPES} from './contants';

import './PhotoEditorBottomSheet.sass';

export const PhotoEditorBottomSheet = ({
  scrollToTop,
  setShowMenu,
  showMenu,
  uyoImage,
  activeDesignToolMenu: activeMenu,
  setActiveDesignToolMenu: setActiveMenu,
}) => {
  const {canvas, activeObjects, setActiveObjects} = useContext(EventCreateContext);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const countOfUyoImages = useSelector(selectCountOfUyoImages);
  const backgroundImage = useSelector(selectFabricArtBackgroundImage);
  const isMultipleUyo = countOfUyoImages > 1;

  const [activeTransform, setActiveTransform] = useState(TRANSFORM_TYPES.ROTATE);

  const {uploading, handleImageUpload} = useImageUpload(canvas);

  const handleClose = useCallback(() => {
    scrollToTop();
    setShowMenu(false);
    canvas.discardActiveObject();
    canvas.renderAll();
    setActiveMenu(MENU_TYPES.MAIN);
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {editingMobile: false},
    });
  }, [canvas, scrollToTop, setShowMenu, dispatch]);

  const handleSwapPhotoUpload = async (event) => {
    const files = event.target ? event.target.files : [];
    const [file] = files;

    if (file) {
      try {
        dispatch({
          type: UPDATE_DESIGN_OPTIONS,
          payload: {activeCardEditor: 'image'},
        });

        const activeObj = canvas.getActiveObject();
        if (!activeObj || activeObj.type !== 'uYOImage') return;

        await handleImageUpload(file, {
          id: activeObj.id,
          callback: () => {
            // You might want to add specific callback logic here
          },
          resetInputRef: fileInputRef,
          isMultipleUyo,
          backgroundImage,
        });
      } catch (error) {
        // Error handling is done in the hook
      }
    }
  };

  const handleMenuItemClick = useCallback(
    (menuType) => {
      if (menuType === 'transform') {
        setActiveMenu(MENU_TYPES.TRANSFORM);
        setActiveTransform(TRANSFORM_TYPES.ROTATE);
      } else if (menuType === 'remove') {
        const activeObj = canvas.getActiveObject();
        if (activeObj && activeObj.type === 'uYOImage') {
          const canvasObjects = canvas.getObjects();
          const {id} = activeObj;

          canvas.remove(activeObj);
          canvas.discardActiveObject();
          canvas.renderAll();
          canvas.fire('object:removed');

          for (const obj of canvasObjects) {
            if (obj.type === 'rect' && obj.fill === 'white' && obj.id === id) {
              obj.set({
                backgroundColor: 'rgb(128,128,128,0.5)',
                fill: 'transparent',
                type: 'uYOImage',
              });
              canvas.renderAll();
              canvas.fire('object:modified');
            }
          }

          dispatch({
            type: UPDATE_DESIGN_OPTIONS,
            payload: {activeCardEditor: 'intro'},
          });

          handleClose();
        }
      } else if (menuType === 'filters') {
        if (!activeObjects) {
          const selectUyoImage = canvas.getObjects().find((obj) => obj.type === 'uYOImage');
          setActiveObjects(selectUyoImage);
          canvas.setActiveObject(selectUyoImage);
          canvas.renderAll();
          canvas.fire('object:modified');
        }
        setActiveMenu(MENU_TYPES.FILTERS);
      } else if (menuType === 'swap') {
        // Directly trigger file input click when swap is selected
        if (fileInputRef.current && !uploading) {
          fileInputRef.current.click();
        }
      }
    },
    [canvas, dispatch, handleClose, uploading]
  );

  const renderMenuContent = () => {
    switch (activeMenu) {
      case MENU_TYPES.TRANSFORM:
        return (
          <TransformMenu
            activeTransform={activeTransform}
            setActiveTransform={setActiveTransform}
            canvas={canvas}
            uyoImage={uyoImage}
          />
        );
      case MENU_TYPES.FILTERS:
        return <ImageFilters />;
      default:
        return <MainPhotoEditorMenu onMenuItemClick={handleMenuItemClick} />;
    }
  };

  if (!showMenu) return null;

  return (
    <div className="photo-editor-bottom-sheet">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg"
        onChange={handleSwapPhotoUpload}
        style={{display: 'none'}}
        aria-hidden="true"
      />
      <div className="photo-editor-bottom-sheet__content">{renderMenuContent()}</div>
    </div>
  );
};
